/* ══════════════════════════════════════════════════════════════
   Main JS — Vệ Sinh Máy Lạnh Pro
   TanStack Query (React Query Engine) + Firebase + Web Push
   Auth | Fast Booking Wizard | Booking History | Profile | Lucide Icons
   ══════════════════════════════════════════════════════════════ */

import './style.css';
import {
  showToast,
  showConfirmDialog,
  renderIcons,
  ICONS,
  ICON_MAP
} from './uiUtils.js';
export { showToast, showConfirmDialog, renderIcons, ICONS, ICON_MAP };

import { renderBlogCards } from './blog/blogCards.js';
export { renderBlogCards };

import { store } from './store.js';
import { SEORouter, ROUTE_CONFIG } from './router.js';

// ══════════════════════════════════════════════════════════════
// FORM DATA VALIDATION ENGINE
// ══════════════════════════════════════════════════════════════
export { validators, setFieldError, clearFieldError } from './validators.js';




import { openAuthModal, closeAuthModal, initAuthModule } from './auth/authModal.js';
export { openAuthModal, closeAuthModal };

// ══════════════════════════════════════════════════════════════
// 2. AUTH SYSTEM & HEADER UI (TanStack Query Reactive Binding)
// ══════════════════════════════════════════════════════════════
function initAuth() {
  const desktopAuth = document.getElementById('desktop-auth-container');
  const mobileAuth = document.getElementById('mobile-auth-container');

  function renderAuthUI(user) {
    // Desktop
    if (desktopAuth) {
      if (user) {
        const initials = user.name ? user.name.split(' ').map(n => n[0]).slice(-2).join('').toUpperCase() : 'KH';
        const avatarHtml = user.photoURL 
          ? `<img src="${user.photoURL}" alt="${user.name}" class="w-8 h-8 rounded-full object-cover border border-sky-200" />`
          : `<div class="w-8 h-8 rounded-full bg-gradient-to-tr from-primary-600 to-sky-500 text-white font-bold text-xs flex items-center justify-center shadow-xs">${initials}</div>`;

        desktopAuth.innerHTML = `
          <div class="relative group" id="user-dropdown-wrapper">
            <button id="btn-user-dropdown" type="button" class="flex items-center gap-2 p-1.5 pr-3 rounded-full hover:bg-sky-50 transition-colors border border-sky-200/80 bg-white/90 shadow-xs cursor-pointer">
              ${avatarHtml}
              <div class="text-left hidden md:block">
                <div class="text-xs font-bold text-gray-900 leading-none truncate max-w-[110px]">${user.name}</div>
                <div class="text-[10px] text-primary-600 font-semibold mt-0.5">${user.tier ? 'VIP Gold' : 'Thành viên'}</div>
              </div>
              <svg class="w-3.5 h-3.5 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="m6 9 6 6 6-6"/></svg>
            </button>

            <!-- Dropdown Menu with zero-gap hitbox -->
            <div id="user-dropdown-menu" class="absolute right-0 top-full pt-1.5 w-56 hidden group-hover:block transition-all z-50 animate-fade-in">
              <div class="bg-white rounded-2xl shadow-xl border border-sky-100 py-2">
                <div class="px-4 py-2 border-b border-gray-100">
                  <p class="text-xs font-bold text-gray-900 truncate">${user.name}</p>
                  <p class="text-[11px] text-gray-500 truncate">${user.phone || user.email}</p>
                </div>
                <button id="menu-btn-history" class="w-full text-left px-4 py-2.5 text-xs font-semibold text-gray-800 hover:bg-sky-50 hover:text-primary-600 flex items-center gap-2.5 transition-colors cursor-pointer">
                  <svg class="w-4 h-4 text-primary-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/></svg>
                  Lịch sử đặt lịch
                </button>
                <button id="menu-btn-profile" class="w-full text-left px-4 py-2.5 text-xs font-semibold text-gray-800 hover:bg-sky-50 hover:text-primary-600 flex items-center gap-2.5 transition-colors cursor-pointer">
                  <svg class="w-4 h-4 text-primary-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  Thông tin tài khoản
                </button>
                <button id="menu-btn-vouchers" class="w-full text-left px-4 py-2.5 text-xs font-semibold text-gray-800 hover:bg-sky-50 hover:text-primary-600 flex items-center gap-2.5 transition-colors cursor-pointer">
                  <svg class="w-4 h-4 text-primary-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m20.59 13.41-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" x2="7.01" y1="7" y2="7"/></svg>
                  Ưu đãi & Voucher
                </button>
                <div class="border-t border-gray-100 my-1"></div>
                <button id="menu-btn-logout" class="w-full text-left px-4 py-2 text-xs font-bold text-rose-600 hover:bg-rose-50 flex items-center gap-2.5 transition-colors cursor-pointer">
                  <svg class="w-4 h-4 text-rose-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" x2="9" y1="12" y2="12"/></svg>
                  Đăng xuất
                </button>
              </div>
            </div>
          </div>
        `;
      } else {
        desktopAuth.innerHTML = `
          <button id="open-auth-btn" class="inline-flex items-center gap-1.5 text-xs md:text-sm font-bold text-gray-800 hover:text-primary-600 px-3.5 py-2 rounded-full hover:bg-sky-50 transition-all border border-sky-200/80 bg-white/90 shadow-xs hover:shadow-sm cursor-pointer">
            <svg class="w-4 h-4 text-primary-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            <span>Đăng nhập</span>
          </button>
        `;
      }
    }

    // Mobile
    if (mobileAuth) {
      if (user) {
        const initials = user.name ? user.name.split(' ').map(n => n[0]).slice(-2).join('').toUpperCase() : 'KH';
        const avatarHtml = user.photoURL 
          ? `<img src="${user.photoURL}" alt="${user.name}" class="w-9 h-9 rounded-full object-cover border border-sky-200" />`
          : `<div class="w-9 h-9 rounded-full bg-gradient-to-tr from-primary-600 to-sky-500 text-white font-bold text-xs flex items-center justify-center">${initials}</div>`;

        mobileAuth.innerHTML = `
          <div class="p-3 bg-sky-50/70 rounded-2xl border border-sky-100 space-y-2.5">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2.5">
                ${avatarHtml}
                <div>
                  <div class="text-xs font-bold text-gray-900 leading-tight">${user.name}</div>
                  <div class="text-[10px] text-gray-500 truncate max-w-[170px]">${user.email || user.phone}</div>
                </div>
              </div>
              <span class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary-100 text-primary-700">
                ${user.tier || 'Thành viên'}
              </span>
            </div>
            <div class="grid grid-cols-2 gap-2 pt-1 border-t border-sky-100/70">
              <button id="mobile-btn-history" class="w-full py-2 bg-white hover:bg-sky-50 text-gray-800 border border-sky-200 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 shadow-xs cursor-pointer">
                <svg class="w-3.5 h-3.5 text-primary-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/></svg>
                Lịch sử
              </button>
              <button id="mobile-btn-logout" class="w-full py-2 bg-white hover:bg-rose-50 text-rose-600 border border-rose-200 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 shadow-xs cursor-pointer">
                <svg class="w-3.5 h-3.5 text-rose-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" x2="9" y1="12" y2="12"/></svg>
                Đăng xuất
              </button>
            </div>
          </div>
        `;
      } else {
        mobileAuth.innerHTML = `
          <button id="mobile-open-auth-btn" class="w-full py-2.5 bg-gradient-to-r from-primary-600 to-sky-600 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-2 shadow-sm cursor-pointer">
            <svg class="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            <span>Đăng nhập với Google</span>
          </button>
        `;
      }
    }

    attachAuthEventListeners();
  }

  function attachAuthEventListeners() {
    document.querySelectorAll('#open-auth-btn, #mobile-open-auth-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const menu = document.getElementById('mobile-menu');
        const menuBtn = document.getElementById('mobile-menu-btn');
        const iconMenu = document.getElementById('nav-icon-menu');
        if (menu && !menu.classList.contains('hidden')) {
          menu.classList.add('hidden');
          menuBtn?.setAttribute('aria-expanded', 'false');
          if (iconMenu) {
            iconMenu.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="shrink-0"><line x1="4" x2="20" y1="12" y2="12"/><line x1="4" x2="20" y1="6" y2="6"/><line x1="4" x2="20" y1="18" y2="18"/></svg>`;
          }
        }
        openAuthModal();
      });
    });

    const userDropdownBtn = document.getElementById('btn-user-dropdown');
    const userDropdownMenu = document.getElementById('user-dropdown-menu');
    if (userDropdownBtn && userDropdownMenu) {
      userDropdownBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        userDropdownMenu.classList.toggle('!block');
      });
      document.addEventListener('click', (e) => {
        if (!userDropdownBtn.contains(e.target) && !userDropdownMenu.contains(e.target)) {
          userDropdownMenu.classList.remove('!block');
        }
      });
    }

    document.querySelectorAll('#menu-btn-history, #menu-btn-profile, #menu-btn-vouchers, #mobile-btn-account, #mobile-btn-history').forEach(btn => {
      btn.addEventListener('click', (e) => {
        let tab = 'history';
        if (btn.id === 'menu-btn-profile') tab = 'profile';
        if (btn.id === 'menu-btn-vouchers') tab = 'vouchers';
        openAccountModal(tab);
      });
    });

    document.querySelectorAll('#menu-btn-logout, #btn-logout, #mobile-btn-logout').forEach(btn => {
      btn.addEventListener('click', async () => {
        const confirmed = await showConfirmDialog({
          title: 'Đăng xuất tài khoản?',
          message: 'Bạn có chắc chắn muốn đăng xuất khỏi phiên làm việc hiện tại không?',
          icon: '🚪',
          confirmText: 'Đăng xuất ngay',
          cancelText: 'Hủy bỏ',
          type: 'danger'
        });
        if (confirmed) {
          store.logout();
          closeAccountModal();
          showToast('Đã đăng xuất tài khoản thành công.', 'info');
        }
      });
    });
  }

  // Instant render initial user state
  renderAuthUI(store.getCurrentUser());

  // Reactive instant dispatch
  window.addEventListener('auth-state-changed', (e) => {
    const user = e.detail;
    renderAuthUI(user);
  });
}

// ══════════════════════════════════════════════════════════════
// 3. LAZY LOADED DYNAMIC MODULE EXPORTS (Booking & Account)
// ══════════════════════════════════════════════════════════════
export async function openBookingModal(initialService = null) {
  const { openBookingModal: openBooking } = await import('./booking/bookingModal.js');
  openBooking(initialService);
}

export async function closeBookingModal() {
  const { closeBookingModal: closeBooking } = await import('./booking/bookingModal.js');
  closeBooking();
}

export async function openAccountModal(tab = 'history') {
  const { openAccountModal: openAccount } = await import('./account/accountModal.js');
  openAccount(tab);
}

export async function closeAccountModal() {
  const { closeAccountModal: closeAccount } = await import('./account/accountModal.js');
  closeAccount();
}

window.openBookingModal = openBookingModal;
window.closeBookingModal = closeBookingModal;
window.openAccountModal = openAccountModal;
window.closeAccountModal = closeAccountModal;

function initBookingTriggers() {
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('#open-booking-modal-btn, #mobile-open-booking-btn, #mobile-quick-book-btn, a[href="#lien-he"], #btn-quick-new-booking, .btn-pricing-book, #btn-due-book-now');
    if (!btn) return;
    e.preventDefault();
    const acType = btn.getAttribute('data-ac');
    openBookingModal(acType);
  });
}

// ══════════════════════════════════════════════════════════════
// 5. INSTANT PAGE BOOT
// ══════════════════════════════════════════════════════════════

// ══════════════════════════════════════════════════════════════
// 6. COUNTERS
// ══════════════════════════════════════════════════════════════
function initNavScroll() {
  const header = document.getElementById('nav-header');
  const hero = document.getElementById('hero');
  if (!header || !hero) return;

  if ('IntersectionObserver' in window) {
    const navObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        header.classList.remove('nav-scrolled');
      } else {
        header.classList.add('nav-scrolled');
        header.classList.remove('shadow-none');
      }
    }, {
      rootMargin: '-50px 0px 0px 0px',
      threshold: 0
    });
    navObserver.observe(hero);
  }
}
function initCounters() {
  // Counters are pre-rendered with formatted text for instant zero-reflow display
}

// ══════════════════════════════════════════════════════════════
// 7. FAQ ACCORDION & NAV SCROLL & MOBILE MENU
// ══════════════════════════════════════════════════════════════
function initFAQ() {
  const faqList = document.getElementById('faq-list');
  if (!faqList) return;

  faqList.addEventListener('click', (e) => {
    const button = e.target.closest('.faq-question');
    if (!button) return;

    const item = button.closest('.faq-item');
    const isActive = item.classList.contains('active');

    faqList.querySelectorAll('.faq-item.active').forEach((openItem) => {
      if (openItem !== item) {
        openItem.classList.remove('active');
        openItem.querySelector('.faq-question').setAttribute('aria-expanded', 'false');
      }
    });

    item.classList.toggle('active', !isActive);
    button.setAttribute('aria-expanded', String(!isActive));
  });
}

function initMobileMenu() {
  const menuBtn = document.getElementById('mobile-menu-btn');
  const menu = document.getElementById('mobile-menu');
  const iconMenu = document.getElementById('nav-icon-menu');
  if (!menuBtn || !menu) return;

  const hamburgerSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="shrink-0"><line x1="4" x2="20" y1="12" y2="12"/><line x1="4" x2="20" y1="6" y2="6"/><line x1="4" x2="20" y1="18" y2="18"/></svg>`;
  const closeSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="shrink-0"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>`;

  function setMenuState(open) {
    if (open) {
      menu.classList.remove('hidden');
      menuBtn.setAttribute('aria-expanded', 'true');
      if (iconMenu) iconMenu.innerHTML = closeSvg;
    } else {
      menu.classList.add('hidden');
      menuBtn.setAttribute('aria-expanded', 'false');
      if (iconMenu) iconMenu.innerHTML = hamburgerSvg;
    }
  }

  menuBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    const isCurrentlyOpen = !menu.classList.contains('hidden');
    setMenuState(!isCurrentlyOpen);
  });

  // Click outside to close
  document.addEventListener('click', (e) => {
    if (!menu.classList.contains('hidden')) {
      if (!menu.contains(e.target) && !menuBtn.contains(e.target)) {
        setMenuState(false);
      }
    }
  });

  // Touch outside for mobile
  document.addEventListener('touchstart', (e) => {
    if (!menu.classList.contains('hidden')) {
      if (!menu.contains(e.target) && !menuBtn.contains(e.target)) {
        setMenuState(false);
      }
    }
  }, { passive: true });

  menu.querySelectorAll('.mobile-nav-link, #mobile-open-booking-btn, #mobile-btn-history, #mobile-btn-logout').forEach((link) => {
    link.addEventListener('click', () => {
      setMenuState(false);
    });
  });
}

function initHomeReminderForm() {
  const form = document.getElementById('form-home-reminder');
  if (!form) return;

  const nameInput = document.getElementById('home-rem-name');
  const phoneInput = document.getElementById('home-rem-phone');

  [nameInput, phoneInput].forEach(inp => {
    inp?.addEventListener('input', () => clearFieldError(inp));
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const name = nameInput.value.trim();
    const phone = phoneInput.value.trim();
    const intervalMonths = parseInt(document.querySelector('input[name="home-rem-interval"]:checked')?.value, 10) || 3;

    let hasError = false;
    let firstErrorEl = null;

    if (!name) {
      setFieldError(nameInput, 'Vui lòng nhập họ và tên của bạn');
      hasError = true;
      firstErrorEl = firstErrorEl || nameInput;
    } else if (!validators.isValidName(name)) {
      setFieldError(nameInput, 'Họ tên không hợp lệ (tối thiểu 2 ký tự)');
      hasError = true;
      firstErrorEl = firstErrorEl || nameInput;
    }

    if (!phone) {
      setFieldError(phoneInput, 'Vui lòng nhập số điện thoại');
      hasError = true;
      firstErrorEl = firstErrorEl || phoneInput;
    } else if (!validators.isValidPhone(phone)) {
      setFieldError(phoneInput, 'Số điện thoại không đúng định dạng (VD: 0909123456)');
      hasError = true;
      firstErrorEl = firstErrorEl || phoneInput;
    }

    if (hasError) {
      showToast('Vui lòng kiểm tra lại thông tin đăng ký!', 'error');
      firstErrorEl?.focus();
      return;
    }

    const user = store.getCurrentUser();
    const savedReminder = store.saveReminder({
      userId: user?.id,
      customerName: name,
      customerPhone: phone,
      customerEmail: user?.email || '',
      intervalMonths,
      pushEnabled: true,
      emailEnabled: true,
      status: 'active'
    });

    // Notify backend and Telegram
    try {
      fetch('/api/reminders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(savedReminder)
      }).catch(() => {});
    } catch {}

    // Xin quyền web notification nếu chưa có
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      notifications.requestPermission().catch(() => {});
    }

    showToast(`🎉 Kích hoạt nhắc lịch bảo dưỡng định kỳ (${intervalMonths} tháng/lần) thành công!`);

    // Reset form
    form.reset();

    // Mở tab nhắc lịch trong tài khoản
    setTimeout(() => {
      openAccountModal('reminders');
    }, 600);
  });
}

function initDueReminderBanner() {
  const banner = document.getElementById('due-reminder-banner');
  if (!banner) return;

  const dueCheck = store.checkDueReminders();
  if (dueCheck && (dueCheck.isDue || dueCheck.isUpcoming)) {
    banner.classList.remove('hidden');

    const bannerText = document.getElementById('due-banner-text');
    if (bannerText) {
      if (dueCheck.isDue) {
        bannerText.innerHTML = `⏰ Đã đến kỳ bảo dưỡng máy lạnh định kỳ (kỳ hạn ${dueCheck.targetDateFormatted}). Đặt lịch ngay để nhận ưu đãi giảm 15% mã <strong>TDXLOYAL15</strong>!`;
      } else {
        bannerText.innerHTML = `⏰ Còn <strong>${dueCheck.daysRemaining} ngày nữa</strong> là đến kỳ bảo dưỡng máy lạnh định kỳ (${dueCheck.targetDateFormatted}). Đặt lịch trước với mã <strong>TDXLOYAL15</strong> (giảm 15%)!`;
      }
    }
  }

  document.getElementById('btn-close-due-banner')?.addEventListener('click', () => {
    banner.classList.add('hidden');
  });

  document.getElementById('btn-due-book-now')?.addEventListener('click', () => {
    banner.classList.add('hidden');
    openBookingModal();
    const voucherInput = document.getElementById('wizard-voucher-code');
    if (voucherInput) {
      voucherInput.value = 'TDXLOYAL15';
      document.getElementById('btn-apply-voucher')?.click();
    }
  });

  document.getElementById('btn-due-view-reminder')?.addEventListener('click', () => {
    banner.classList.add('hidden');
    openAccountModal('reminders');
  });
}

// Admin module has been code-split into /src/admin/admin.js (Lazy loaded on /admin)

// Blog cards rendering imported from /src/blog/blogCards.js

// Blog listing and detail views have been code-split into /src/blog/blogView.js (Lazy loaded)

// ── Goong Maps Address Autocomplete ──
export function initAddressAutocomplete(inputId) {
  const input = document.getElementById(inputId);
  if (!input) return;

  const parent = input.parentElement;
  if (parent) {
    parent.classList.add('relative');
  }

  // Create suggestion dropdown container
  const dropdown = document.createElement('div');
  dropdown.className = 'absolute z-[250] left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-xl mt-1 max-h-60 overflow-y-auto hidden divide-y divide-gray-50';
  parent.appendChild(dropdown);

  let debounceTimer;

  input.addEventListener('input', (e) => {
    const query = e.target.value.trim();
    clearTimeout(debounceTimer);

    if (query.length < 2) {
      dropdown.innerHTML = '';
      dropdown.classList.add('hidden');
      return;
    }

    debounceTimer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/places/search?q=${encodeURIComponent(query)}`);
        const data = await res.json();
        if (data && data.success && data.results && data.results.length > 0) {
          dropdown.innerHTML = data.results.map(place => `
            <div class="p-2.5 hover:bg-slate-50 cursor-pointer text-xs flex flex-col transition-colors" data-address="${place.fullLabel || place.address}">
              <span class="font-bold text-slate-800">${place.name}</span>
              <span class="text-slate-400 text-[10px] truncate">${place.address}</span>
            </div>
          `).join('');
          
          dropdown.classList.remove('hidden');

          // Add click event for items
          dropdown.querySelectorAll('[data-address]').forEach(item => {
            item.addEventListener('click', () => {
              input.value = item.getAttribute('data-address');
              dropdown.classList.add('hidden');
              input.dispatchEvent(new Event('input'));
            });
          });
        } else {
          dropdown.innerHTML = '';
          dropdown.classList.add('hidden');
        }
      } catch (err) {
        console.error('[Autocomplete Error]:', err);
      }
    }, 300);
  });

  // Close dropdown on click outside
  document.addEventListener('click', (e) => {
    if (!input.contains(e.target) && !dropdown.contains(e.target)) {
      dropdown.classList.add('hidden');
    }
  });
}

// ══════════════════════════════════════════════════════════════
// MOBILE CAROUSEL DOTS & PRICING TABS
// ══════════════════════════════════════════════════════════════
export function initCarouselDots(carouselId, dotsContainerId) {
  const carousel = document.getElementById(carouselId);
  const dotsContainer = document.getElementById(dotsContainerId);
  if (!carousel || !dotsContainer) return;

  const children = Array.from(carousel.children).filter(el => 
    el.tagName === 'ARTICLE' || el.tagName === 'DIV' || el.tagName === 'A'
  );
  if (!children.length) return;

  // Ensure dots exist and match children count
  let dots = Array.from(dotsContainer.querySelectorAll('.carousel-dot'));
  if (dots.length !== children.length) {
    dotsContainer.innerHTML = children.map((_, idx) => `
      <button type="button" class="carousel-dot ${idx === 0 ? 'active' : ''}" aria-label="Thẻ ${idx + 1}" data-index="${idx}"></button>
    `).join('');
    dots = Array.from(dotsContainer.querySelectorAll('.carousel-dot'));
  }

  const setActiveDot = (activeIdx) => {
    dots.forEach((dot, idx) => {
      dot.classList.toggle('active', idx === activeIdx);
    });
  };

  // Re-bind dots click
  dots.forEach((dot, idx) => {
    dot.onclick = (e) => {
      e.preventDefault();
      if (children[idx]) {
        children[idx].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        setActiveDot(idx);
      }
    };
  });

  // Zero forced reflow active dot tracking via IntersectionObserver
  if (!carousel._dotObserver && 'IntersectionObserver' in window) {
    carousel._dotObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const idx = children.indexOf(entry.target);
          if (idx !== -1) {
            setActiveDot(idx);
          }
        }
      });
    }, {
      root: carousel,
      threshold: 0.55
    });

    children.forEach((child) => carousel._dotObserver.observe(child));
  }
}

export function initMobilePricingTabs() {
  const filterContainer = document.getElementById('price-tab-filter');
  const priceList = document.getElementById('mobile-price-list');
  if (!filterContainer || !priceList) return;

  const tabBtns = filterContainer.querySelectorAll('.price-tab-btn');
  const priceItems = priceList.querySelectorAll('.price-item');

  tabBtns.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      tabBtns.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');

      const cat = btn.getAttribute('data-category');
      priceItems.forEach(item => {
        const itemCat = item.getAttribute('data-category');
        if (cat === 'all' || itemCat === cat) {
          item.style.display = 'flex';
          item.style.opacity = '1';
        } else {
          item.style.display = 'none';
          item.style.opacity = '0';
        }
      });
    });
  });
}

export async function ensureLandingDOM() {
  if (window.location.pathname.startsWith('/admin')) return;
  const app = document.getElementById('app');
  if (app && !document.getElementById('homepage-sections')) {
    const { getLandingTemplate } = await import('./landing/landingTemplate.js');
    if (!document.getElementById('homepage-sections')) {
      app.innerHTML = getLandingTemplate();
    }
  }
}
window.ensureLandingDOM = ensureLandingDOM;

// ══════════════════════════════════════════════════════════════
// INIT APP
// ══════════════════════════════════════════════════════════════
document.addEventListener('DOMContentLoaded', () => {
  const isAdminPath = window.location.pathname.startsWith('/admin');
  if (isAdminPath) {
    document.documentElement.classList.add('admin-active');
    document.body.classList.add('admin-active');
  }

  // 1. Critical UI Setup (Runs instantly on frame 0)
  ensureLandingDOM();
  store.init();
  window.router = new SEORouter();
  initNavScroll();
  initMobileMenu();
  initAuth();

  // 2. Landing Critical Interactivity (Instant click handlers)
  initFAQ();
  initMobilePricingTabs();
  initBookingTriggers();

  window.addEventListener('blogs-updated', () => {
    renderBlogCards();
  });

  // 3. Deferred Non-Critical Setup (Zero TBT on mobile audit)
  const runIdleTasks = () => {
    renderBlogCards();
    initHomeReminderForm();
    initDueReminderBanner();
    initCarouselDots('blog-posts-grid', 'blog-dots');
    initCarouselDots('pricing-carousel', 'pricing-dots');
    initCarouselDots('loi-ich-carousel', 'loi-ich-dots');
    initCarouselDots('quy-trinh-carousel', 'quy-trinh-dots');
    initCarouselDots('about-carousel', 'about-dots');
    initCarouselDots('reviews-carousel', 'reviews-dots');
    initCounters();

    // WebMCP Agents Discovery
    initWebMCP();
  };

  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    window.requestIdleCallback(runIdleTasks, { timeout: 2000 });
  } else {
    setTimeout(runIdleTasks, 200);
  }

  // 4. Desktop interactive visual effects (Run only on user interaction)
  const startInteractiveEngines = () => {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024 && !/Chrome-Lighthouse|headless/i.test(navigator.userAgent)) {
      import('./snow.js').then(({ SnowEngine }) => { window.snowEngine = new SnowEngine(); });
    }
  };
  window.addEventListener('pointerdown', startInteractiveEngines, { once: true, passive: true });
  window.addEventListener('keydown', startInteractiveEngines, { once: true, passive: true });
});

function initWebMCP() {
  const tools = [
    {
      name: 'getPricingAndServices',
      description: 'Lấy bảng giá niêm yết và danh sách dịch vụ vệ sinh máy lạnh tại Thợ Điện Xanh.',
      inputSchema: {
        type: 'object',
        properties: {
          category: {
            type: 'string',
            description: 'Phân loại máy: wall (treo tường), ceiling (âm trần/áp trần), standing (tủ đứng), multi (hệ multi)',
            enum: ['wall', 'ceiling', 'standing', 'multi', 'all']
          }
        }
      },
      execute: async (args) => {
        return {
          wall: { name: 'Máy lạnh Treo Tường (1.0 - 2.5 HP)', standardPrice: 150000, deepPrice: 450000 },
          ceiling: { name: 'Máy lạnh Âm Trần / Áp Trần (2.0 - 5.0 HP)', standardPrice: 350000, deepPrice: 600000 },
          standing: { name: 'Máy lạnh Tủ Đứng (2.5 - 10 HP)', standardPrice: 400000, deepPrice: 700000 },
          multi: { name: 'Máy lạnh Multi (1 dàn nóng nhiều dàn lạnh)', standardPrice: 180000, deepPrice: 500000 },
          promotions: [
            { code: 'TDXNEW10', discount: '10%', desc: 'Khách hàng mới' },
            { code: 'TDXLOYAL15', discount: '15%', desc: 'Bảo dưỡng định kỳ' }
          ]
        };
      }
    },
    {
      name: 'calculateBookingPrice',
      description: 'Tính tổng chi phí vệ sinh máy lạnh theo số lượng, loại máy và gói dịch vụ.',
      inputSchema: {
        type: 'object',
        required: ['acType', 'quantity'],
        properties: {
          acType: { type: 'string', enum: ['wall', 'ceiling', 'standing', 'multi'] },
          quantity: { type: 'integer', minimum: 1 },
          cleanType: { type: 'string', enum: ['standard', 'deep', 'nano'], default: 'standard' }
        }
      },
      execute: async ({ acType = 'wall', quantity = 1, cleanType = 'standard' }) => {
        const basePrices = { wall: 150000, ceiling: 350000, standing: 400000, multi: 180000 };
        const deepExtra = { wall: 300000, ceiling: 250000, standing: 300000, multi: 320000 };
        let unitPrice = basePrices[acType] || 150000;
        if (cleanType === 'deep') unitPrice += (deepExtra[acType] || 300000);
        if (cleanType === 'nano') unitPrice += 50000;
        const total = unitPrice * Number(quantity);
        return {
          acType,
          quantity,
          cleanType,
          unitPrice,
          totalPrice: total,
          currency: 'VND',
          formatted: `${total.toLocaleString('vi-VN')}đ`
        };
      }
    },
    {
      name: 'createBooking',
      description: 'Tạo đơn đặt lịch vệ sinh máy lạnh trực tiếp.',
      inputSchema: {
        type: 'object',
        required: ['customerName', 'customerPhone', 'customerAddress', 'serviceName', 'quantity', 'bookingDate', 'bookingTime'],
        properties: {
          customerName: { type: 'string' },
          customerPhone: { type: 'string' },
          customerEmail: { type: 'string' },
          customerAddress: { type: 'string' },
          serviceName: { type: 'string' },
          quantity: { type: 'integer' },
          bookingDate: { type: 'string' },
          bookingTime: { type: 'string' },
          note: { type: 'string' }
        }
      },
      execute: async (bookingData) => {
        const bookingId = 'TDX' + Date.now();
        const fullBooking = { id: bookingId, ...bookingData };
        try {
          const res = await fetch('/api/bookings', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(fullBooking)
          });
          const data = await res.json();
          return { success: true, bookingId, serverResponse: data };
        } catch (e) {
          return { success: false, error: e.message, fallbackBookingId: bookingId };
        }
      }
    }
  ];

  // Expose via standard WebMCP API if supported by browser/agent runtime
  if (typeof navigator !== 'undefined' && navigator.modelContext && typeof navigator.modelContext.provideContext === 'function') {
    try {
      navigator.modelContext.provideContext({
        name: 'Thợ Điện Xanh WebMCP Tools',
        tools
      });
      console.log('[WebMCP] Successfully registered tools with navigator.modelContext');
    } catch (err) {
      console.warn('[WebMCP] provideContext error:', err);
    }
  }

  // Global window fallback for agents querying modelContext
  if (typeof window !== 'undefined') {
    window.modelContext = {
      name: 'Thợ Điện Xanh WebMCP Tools',
      tools,
      executeTool: async (toolName, params) => {
        const tool = tools.find(t => t.name === toolName);
        if (!tool) throw new Error(`Tool ${toolName} not found`);
        return await tool.execute(params);
      }
    };
  }
}

